import { type ProcessStepRecordingDisplay } from '@bliks-io/contracts';
import { getDecisionTableColumns, getRuleInputValue } from '@bliks-io/ui';
import { formatStepName, compareByStepNumber, type CellValue } from './processStepsExcel';

/**
 * The `Verzweigungen` (branches) sheet of the Process Detail Excel export — the
 * edge half of the nodes-and-edges workbook. One row per outgoing edge: a task
 * step contributes a single edge (its `task.output`), a decision step one edge
 * per rule (that rule's folded conditions + `rule.output`). All step-level
 * metadata stays on the `Schritte` sheet, so the multiplicity that used to
 * duplicate steps lives here and only here.
 *
 * The successor of an edge is NOT `rules[].successors` / `task.successors`
 * (those name who performs the next step, not the next node). The real
 * step→step wiring is the inverse of `inputs[].sourceSteps[]`: a step T whose
 * input names `{ stepId: S, ruleIds }` is a successor of S, reached via S's
 * rule(s). This mirrors the canonical forward-graph build in
 * `packages/ui/.../process-flow/core/builder.ts` (the flow builder itself is
 * too entangled with gateway/handle/layout synthesis to reuse directly).
 *
 * Only real steps are emitted as source rows, so an edge whose source is a
 * start-event node (an event input's `sourceSteps`) — i.e. the process-entry
 * edge — is intentionally absent: this is a step→step sheet.
 */

type DecisionRule = NonNullable<ProcessStepRecordingDisplay['rules']>[number];

/** Localized header labels for the branches sheet. */
export interface ProcessEdgesSheetLabels {
  stepNumber: string;
  processStep: string;
  conditions: string;
  output: string;
  successorNumber: string;
  successor: string;
}

// Mirrors `getPreviousStepId` in the flow builder: the populated `previousStep`
// may be a string id, null, or a populated step carrying `_id`.
function previousStepId(previousStep: ProcessStepRecordingDisplay['previousStep']): string | null {
  if (!previousStep) return null;
  if (typeof previousStep === 'object') return previousStep._id;
  return previousStep;
}

// Resolve a source decision's `ruleIds` to the distinct rule outputs they touch.
// Edges are keyed by output (the decision's handle), so two rules sharing an
// output share a successor; a stale ruleId that no longer resolves is dropped.
function resolveRuleOutputs(
  sourceStep: ProcessStepRecordingDisplay | undefined,
  ruleIds: string[]
): string[] {
  const outputById = new Map(
    (sourceStep?.rules ?? [])
      .filter((rule): rule is DecisionRule & { _id: string } => !!rule._id)
      .map((rule) => [String(rule._id), rule.output])
  );
  const outputs: string[] = [];
  const seen = new Set<string>();
  for (const ruleId of ruleIds) {
    const output = outputById.get(String(ruleId));
    if (output === undefined || seen.has(output)) continue;
    seen.add(output);
    outputs.push(output);
  }
  return outputs;
}

interface ForwardGraph {
  stepById: Map<string, ProcessStepRecordingDisplay>;
  /** task source id → successor step ids (rule-less edges). */
  taskTargets: Map<string, Set<string>>;
  /** decision source id → rule output → successor step ids. */
  decisionTargetsByOutput: Map<string, Map<string, Set<string>>>;
}

function buildForwardGraph(steps: ProcessStepRecordingDisplay[]): ForwardGraph {
  const stepById = new Map(steps.map((step) => [step._id, step]));
  const decisionIds = new Set(
    steps.filter((step) => step.actionType === 'decision').map((step) => step._id)
  );

  const taskTargets = new Map<string, Set<string>>();
  const decisionTargetsByOutput = new Map<string, Map<string, Set<string>>>();

  const addTask = (sourceId: string, targetId: string) => {
    if (!taskTargets.has(sourceId)) taskTargets.set(sourceId, new Set());
    taskTargets.get(sourceId)!.add(targetId);
  };
  const addDecision = (sourceId: string, output: string, targetId: string) => {
    if (!decisionTargetsByOutput.has(sourceId)) decisionTargetsByOutput.set(sourceId, new Map());
    const byOutput = decisionTargetsByOutput.get(sourceId)!;
    if (!byOutput.has(output)) byOutput.set(output, new Set());
    byOutput.get(output)!.add(targetId);
  };

  const addEdgesFromSource = (source: { stepId: string; ruleIds?: string[] }, targetId: string) => {
    const sourceId = String(source.stepId);
    if (!decisionIds.has(sourceId)) {
      addTask(sourceId, targetId);
      return;
    }
    // An unbound decision edge (no ruleIds) belongs to no output — drop it,
    // exactly as the flow builder does.
    if (!source.ruleIds || source.ruleIds.length === 0) return;
    for (const output of resolveRuleOutputs(stepById.get(sourceId), source.ruleIds)) {
      addDecision(sourceId, output, targetId);
    }
  };

  for (const target of steps) {
    const inputs = target.inputs ?? [];
    // Only steps with no `previous_output` input fall back to the legacy
    // `previousStep` pointer (ADR 0006); a present input is authoritative.
    const hasPreviousOutputInput = inputs.some((input) => input.inputType === 'previous_output');

    for (const input of inputs) {
      for (const source of input.sourceSteps ?? []) addEdgesFromSource(source, target._id);
    }

    if (!hasPreviousOutputInput) {
      const prevId = previousStepId(target.previousStep);
      // Guard the same hazard as the builder: a stale previousStep into a
      // decision would synthesize a phantom, output-less successor.
      if (prevId && !decisionIds.has(prevId)) addTask(prevId, target._id);
    }
  }

  return { stepById, taskTargets, decisionTargetsByOutput };
}

// Fold a decision rule's condition cells into one string, in the step-wide
// column order so the text reads consistently across the step's rules; empty
// cells (a rule that doesn't constrain a column) are dropped to avoid noise.
// `columns` is the step-wide column order, computed once per step by the caller.
function foldConditions(columns: string[], rule: DecisionRule): string {
  return columns
    .map((column) => {
      const value = getRuleInputValue(rule.inputs, column);
      return value ? `${column}: ${value}` : null;
    })
    .filter((entry): entry is string => entry !== null)
    .join('; ');
}

/**
 * Build the branches worksheet as an array-of-arrays (header + one row per
 * outgoing edge, grouped by source step in step-number order). `Prozessschritt`
 * and `Folgeschritt` carry the resolved names as plain values; the workbook
 * assembler overlays VLOOKUP formulas so they also recompute on rename. Pure —
 * no SheetJS — so it is unit-testable on its own.
 */
export function buildProcessEdgeRows(
  steps: ProcessStepRecordingDisplay[],
  options: { labels: ProcessEdgesSheetLabels }
): { rows: CellValue[][]; colWidths: { wch: number }[] } {
  const { labels } = options;
  const { stepById, taskTargets, decisionTargetsByOutput } = buildForwardGraph(steps);

  const stepNumberOf = (id: string): number =>
    stepById.get(id)?.stepNumber ?? Number.MAX_SAFE_INTEGER;
  const sortedTargets = (ids: Set<string> | undefined): string[] =>
    [...(ids ?? [])].sort((a, b) => stepNumberOf(a) - stepNumberOf(b));

  const columns: { header: string; width: number }[] = [
    { header: labels.stepNumber, width: 10 },
    { header: labels.processStep, width: 36 },
    { header: labels.conditions, width: 40 },
    { header: labels.output, width: 32 },
    { header: labels.successorNumber, width: 14 },
    { header: labels.successor, width: 36 }
  ];

  const sortedSteps = [...steps].sort(compareByStepNumber);

  const rows: CellValue[][] = [columns.map((column) => column.header)];

  const pushEdge = (
    step: ProcessStepRecordingDisplay,
    conditions: string,
    output: string,
    targets: string[]
  ) => {
    const stepNumber = step.stepNumber ?? '';
    const stepName = formatStepName(step);
    if (targets.length === 0) {
      rows.push([stepNumber, stepName, conditions, output, '', '']);
      return;
    }
    for (const targetId of targets) {
      const target = stepById.get(targetId);
      rows.push([
        stepNumber,
        stepName,
        conditions,
        output,
        target?.stepNumber ?? '',
        target ? formatStepName(target) : ''
      ]);
    }
  };

  for (const step of sortedSteps) {
    if (step.actionType === 'decision') {
      const rules = step.rules ?? [];
      if (rules.length === 0) {
        pushEdge(step, '', '', []);
        continue;
      }
      const byOutput = decisionTargetsByOutput.get(step._id);
      const decisionColumns = getDecisionTableColumns(rules);
      for (const rule of rules) {
        const output = rule.output ?? '';
        pushEdge(
          step,
          foldConditions(decisionColumns, rule),
          output,
          sortedTargets(byOutput?.get(output))
        );
      }
    } else {
      pushEdge(step, '', step.task?.output ?? '', sortedTargets(taskTargets.get(step._id)));
    }
  }

  return { rows, colWidths: columns.map((column) => ({ wch: column.width })) };
}
